import { decodeJWT } from '@codezo/auth';
import { useSwitchOrganizationMutation } from '@codezo/graphql';
import { useCallback, useEffect, useMemo } from 'react';
import { useAuth } from './useAuth';

/*
 * Moving the session to another organization, and keeping every tab in step.
 *
 * After `switchOrganization` the app hard-navigates instead of storing the
 * returned access token: that token lives in memory only, so a reload would
 * drop it anyway, and the reload re-authenticates from the refresh cookie the
 * mutation already rotated into the new organization. The hard navigation is
 * also what clears every query, and the notification subscription, that
 * belonged to the old organization.
 *
 * Every tab shares that one refresh cookie, so the others' next token refresh
 * lands them in the new organization too — while they keep showing the old
 * one's screens. The switching tab announces it and every other tab reloads.
 * The message carries only the target organization id, never a token.
 */

const CHANNEL_NAME = 'organization-switch';

/**
 * The organization this session works in, read from the access token's
 * `organizationId` claim — the backend's source of truth for it.
 *
 * Preferred over `user.organizationId`, which comes from the auth response's
 * `organizationUser`. The backend resolves that from the issued session's
 * `sessionOrganizationId`, but before that fix it fell back to the account's
 * *oldest* membership after a reload — the claim can't drift that way.
 */
export function useSessionOrganizationId(): string | undefined {
	const { token, user } = useAuth();
	return useMemo(() => {
		const claim = token ? decodeJWT(token)?.organizationId : undefined;
		return typeof claim === 'string' ? claim : user?.organizationId;
	}, [token, user?.organizationId]);
}

type SwitchMessage = { organizationId: string };

function openChannel(): BroadcastChannel | null {
	return typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel(CHANNEL_NAME);
}

function broadcastOrganizationSwitch(organizationId: string): void {
	const channel = openChannel();
	if (!channel) return;
	const message: SwitchMessage = { organizationId };
	channel.postMessage(message);
	channel.close();
}

/**
 * Returns `switchTo(organizationId, destination)`. On success the page is
 * replaced by `destination` (an in-app path) and the promise never matters; on
 * failure it rejects with the mutation's error — typically
 * FORBIDDEN_PERMISSIONS for a membership that no longer exists. An expired
 * session is handled by the global error link.
 */
export function useSwitchOrganization(): (
	organizationId: string,
	destination?: string
) => Promise<void> {
	const [switchOrganization] = useSwitchOrganizationMutation();

	return useCallback(
		async (organizationId: string, destination = '/dashboard') => {
			await switchOrganization({ variables: { organizationId } });
			broadcastOrganizationSwitch(organizationId);
			// `replace`, so Back doesn't return to a screen of the old organization.
			window.location.replace(destination);
		},
		[switchOrganization]
	);
}

/**
 * Reloads this tab into `returnPath` (the top of its current section) when
 * another tab moves the session to a different organization.
 */
export function useOrganizationSwitchSync(
	currentOrganizationId: string | undefined,
	returnPath: string
): void {
	useEffect(() => {
		const channel = openChannel();
		if (!channel) return;
		channel.onmessage = (event: MessageEvent<SwitchMessage>) => {
			if (event.data?.organizationId !== currentOrganizationId) {
				window.location.replace(returnPath);
			}
		};
		return () => channel.close();
	}, [currentOrganizationId, returnPath]);
}
